import React, { useMemo, useState, useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Button, Typography, Tooltip } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { ActionsGroup, SelectFine } from '../../components';
import { unstable_useMediaQuery as useMediaQuery } from '@material-ui/core/useMediaQuery';

const SearchActionsGroup = ({
  classes,
  data,
  isFileUploading,
  isLoggedIn,
  handleToBasket,
  t,
  deliveryTariffList,
  deliveryTariff,
  handleDeliveryTariffChange,
  currencyList,
  currency,
  handleChangeCurrency,
  handleToExcel,
}) => {
  const isMobile = useMediaQuery('(max-width: 600px)');

  // State for click-triggered tooltip
  const [tooltipClickedOpen, setTooltipClickedOpen] = useState(false);
  // State for hover/focus-triggered tooltip
  const [tooltipHoverOpen, setTooltipHoverOpen] = useState(false);

  // Tooltip is open if either clicked or hovered/focused
  const isTooltipOpen = tooltipClickedOpen || tooltipHoverOpen;

  const handleTooltipClose = useCallback(() => {
    setTooltipClickedOpen(false);
    setTooltipHoverOpen(false);
  }, []);

  const handleTooltipClickToggle = useCallback(
    (event) => {
      if (!isMobile && !tooltipClickedOpen) return;
      event.stopPropagation();
      setTooltipClickedOpen((prev) => !prev);
      // If opening via click, ensure hover state doesn't interfere initially
      if (!tooltipClickedOpen) {
        setTooltipHoverOpen(false);
      }
    },
    [tooltipClickedOpen]
  );

  const handleIconMouseEnter = useCallback(() => {
    setTooltipHoverOpen(true);
  }, []);

  const handleIconMouseLeave = useCallback(() => {
    setTooltipHoverOpen(false);
  }, []);

  const basketButton = useMemo(
    () => (
      <Button
        disabled={!data.length || isFileUploading || !isLoggedIn}
        className={classes.basketButton}
        variant="contained"
        color="primary"
        onClick={handleToBasket}
      >
        {t('to_cart')}
      </Button>
    ),
    [
      data.length,
      isFileUploading,
      isLoggedIn,
      handleToBasket,
      t,
      classes.basketButton,
    ]
  );

  const deliveryTariffSelect = useMemo(
    () => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body2">{t('delivery_to')}</Typography>
        <SelectFine
          items={deliveryTariffList}
          value={deliveryTariff}
          onChange={handleDeliveryTariffChange}
        />
        <Tooltip
          onClose={handleTooltipClose}
          open={isTooltipOpen}
          title={t('delivery_to_tooltip')}
          disableFocusListener // We handle focus on the span
          disableHoverListener // We handle hover on the span
          disableTouchListener // We handle click/touch on the span
          // Removing PopperProps={{ disablePortal: true }} to help with click-away
        >
          <span
            onClick={handleTooltipClickToggle}
            onMouseEnter={handleIconMouseEnter}
            onMouseLeave={handleIconMouseLeave}
            onFocus={handleIconMouseEnter} // Show on focus
            onBlur={handleIconMouseLeave} // Hide on blur (if not click-opened)
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleTooltipClickToggle(e);
              }
            }}
          >
            <HelpOutlineIcon />
          </span>
        </Tooltip>
      </div>
    ),
    [
      deliveryTariffList,
      deliveryTariff,
      handleDeliveryTariffChange,
      t,
      isTooltipOpen,
      handleTooltipClose,
      handleTooltipClickToggle,
      handleIconMouseEnter,
      handleIconMouseLeave,
    ]
  );

  const currencySelect = useMemo(
    () => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body2">{t('currency')}</Typography>
        <SelectFine
          items={currencyList}
          value={currency}
          onChange={handleChangeCurrency}
        />
      </div>
    ),
    [currencyList, currency, handleChangeCurrency, t]
  );

  const excelButton = useMemo(
    () => (
      <Button
        disabled={!data.length || isFileUploading}
        className={classes.excelButton}
        variant="contained"
        color="default"
        onClick={handleToExcel}
      >
        {t('export')}
      </Button>
    ),
    [data.length, isFileUploading, handleToExcel, t, classes.excelButton]
  );

  return (
    <ActionsGroup className={classes.actionsGroup}>
      {isMobile ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            {deliveryTariffSelect}
            {currencySelect}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            {basketButton}
            {excelButton}
          </div>
        </div>
      ) : (
        <>
          {basketButton}
          {deliveryTariffSelect}
          {currencySelect}
          {excelButton}
        </>
      )}
    </ActionsGroup>
  );
};

const styles = (theme) => ({
  actionsGroup: {
    justifyContent: 'space-between',
  },
  basketButton: {},
  excelButton: {},
});

export default withStyles(styles)(SearchActionsGroup);
